// Scanner como overlay fullscreen. Port de mockups/scanner-test/app.js,
// adaptado a "abrir/cerrar como API".
//
// Uso:
//   const close = await openScanner({
//     onDecode: (value, format) => { ... return 'consume' | 'continue' },
//     onClose: () => { ... }
//   });
//
// `onDecode` puede devolver 'consume' para parar la cámara y cerrar el overlay
// automáticamente (típico al matchear un EAN del pedido), o 'continue' para
// seguir leyendo (escenario raro: scan adicional, debug).
//
// Mantenemos zxing cargado de esm.sh igual que el banco de pruebas — así el
// frontend principal sigue sin acoplarse al bundler para esta lib pesada y
// rige el mismo binding ya probado en Fase 2.
//
// La cámara se cierra explícitamente con `close()` (devuelto por openScanner)
// o tap en la X del overlay; al desmontar la vista de empaquetado también
// se debe llamar `close()` para liberar el track.

import { BrowserMultiFormatReader } from 'https://esm.sh/@zxing/browser@0.1.5';
import { BarcodeFormat, DecodeHintType } from 'https://esm.sh/@zxing/library@0.21.3';
import { icon } from './icons.js';

// Dedup entre lecturas del MISMO valor. 2s es un compromiso razonable:
// suficiente para que el operario aleje el código y vuelva a apuntarlo
// (escaneo continuo de 24 cajas iguales sin riesgo de contar doble), y
// corto para no bloquear demasiado si la lectura inicial fue por error.
const DEDUP_MS = 2000;

const HINTS = new Map();
HINTS.set(DecodeHintType.TRY_HARDER, true);
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF,
  BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX,
]);

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

function _beep() {
  try {
    const ctx = window.__pickingAudioCtx ||= new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880; o.type = 'square'; g.gain.value = 0.08;
    o.connect(g).connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.06);
  } catch {}
}
function _vibrate(ms = 50) { navigator.vibrate?.(ms); }

/** Abre el scanner. Devuelve función `close()` para cerrar desde fuera.
 *
 *  Acción de `onDecode(value, format) → 'consume' | 'continue'`:
 *    - 'consume' cierra la cámara (modo: una lectura y fuera).
 *    - 'continue' mantiene la cámara abierta para escaneos sucesivos.
 *
 *  Para feedback en pantalla durante modo continuo, el caller puede llamar
 *  a la función `setStatus(text)` que devolvemos junto con `close` para
 *  pintar una banda inferior (ej. "Aceite oliva 1L · 3/12"). */
export async function openScanner({ onDecode, onClose, hint = 'Apunta al código de barras' } = {}) {
  if (!window.isSecureContext) {
    alert('La cámara requiere HTTPS.');
    return () => {};
  }

  const overlay = document.createElement('div');
  overlay.className = 'camera-overlay';
  overlay.innerHTML = `
    <div class="topinfo">
      <span class="badge" style="display:inline-flex;align-items:center;gap:0.4rem;">
        <span class="ico" style="display:inline-flex;">${icon('camera', { size: 18 })}</span>
        ${escapeHtml(hint)}
      </span>
      <span class="fps" id="cam-meta">…</span>
    </div>
    <div class="stream" id="cam-stream">
      <video id="cam-video" playsinline autoplay muted style="width:100%;height:100%;object-fit:cover;background:#000;"></video>
      <div class="reticle"></div>
    </div>
    <!-- Banda de feedback de la última lectura (oculta hasta el primer scan). -->
    <div id="cam-status" hidden style="
      position:absolute; left:0; right:0; bottom: 88px;
      background: rgba(4, 120, 87, 0.92); color: #fff;
      padding: 0.65rem 1rem; text-align: center;
      font-size: var(--fs-base); font-weight: 600;
      box-shadow: 0 -4px 16px rgba(0,0,0,0.3);
    "></div>
    <div class="bottombar">
      <button class="torchbtn" id="cam-torch" title="Linterna" hidden style="display:inline-flex;align-items:center;justify-content:center;">${icon('flashlight', { size: 22 })}</button>
      <button class="torchbtn" id="cam-zoom-out" title="Zoom −" hidden>−</button>
      <button class="torchbtn" id="cam-zoom-in" title="Zoom +" hidden>+</button>
      <button class="torchbtn" id="cam-swap" title="Cambiar lente" hidden style="display:inline-flex;align-items:center;justify-content:center;">${icon('rotate-cw', { size: 22 })}</button>
      <span style="flex:1"></span>
      <button class="closebtn" id="cam-close" title="Cerrar" aria-label="Cerrar" style="display:inline-flex;align-items:center;justify-content:center;">${icon('x', { size: 22 })}</button>
    </div>
  `;
  document.body.appendChild(overlay);

  const video = overlay.querySelector('#cam-video');
  const camMeta = overlay.querySelector('#cam-meta');
  const btnTorch = overlay.querySelector('#cam-torch');
  const btnSwap = overlay.querySelector('#cam-swap');
  const btnZoomIn = overlay.querySelector('#cam-zoom-in');
  const btnZoomOut = overlay.querySelector('#cam-zoom-out');

  const state = {
    reader: null,
    stream: null,
    scanTimer: null,
    devices: [],
    deviceIdx: 0,
    lastValue: null,
    lastTs: 0,
    torchOn: false,
    closed: false,
    zoom: { supported: false, min: 1, max: 1, step: 0.5, current: 1 },
  };

  // Canvas off-screen para recortar la región del reticle antes de decodificar.
  // ZXing por defecto escanea TODO el frame del video — pasamos solo el área
  // del recuadro para que el operario tenga control de qué se lee.
  const roiCanvas = document.createElement('canvas');
  const roiCtx = roiCanvas.getContext('2d', { willReadFrequently: true });
  const reticleEl = overlay.querySelector('.reticle');
  const streamEl = overlay.querySelector('.stream');

  function close() {
    if (state.closed) return;
    state.closed = true;
    if (state.scanTimer) { clearTimeout(state.scanTimer); state.scanTimer = null; }
    try { state.stream?.getTracks().forEach(t => t.stop()); } catch {}
    state.stream = null;
    try { overlay.remove(); } catch {}
    onClose?.();
  }

  overlay.querySelector('#cam-close').addEventListener('click', close);

  state.reader = new BrowserMultiFormatReader(HINTS);

  // Enumerar cámaras (solo traseras si conocemos labels)
  let allDevs = [];
  try { allDevs = await BrowserMultiFormatReader.listVideoInputDevices(); } catch {}
  const rears = allDevs.filter(d => {
    const l = (d.label || '').toLowerCase();
    if (/front|user|self/.test(l)) return false;
    if (/back|rear|environment/.test(l)) return true;
    return l === '';
  });
  state.devices = rears.length ? rears : allDevs;
  btnSwap.hidden = state.devices.length < 2;

  /** Calcula la región (sx,sy,sw,sh) del frame del video que corresponde al
   *  recuadro reticle visible, considerando object-fit: cover del <video>.
   *  Devuelve null si aún no hay dimensiones válidas. */
  function _computeROI() {
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) return null;
    const sBcr = streamEl.getBoundingClientRect();
    const rBcr = reticleEl.getBoundingClientRect();
    if (!sBcr.width || !sBcr.height) return null;

    // object-fit: cover → el video se escala MAX(width/vw, height/vh)
    // y se centra. Los bordes que sobran del frame se "cortan" en el display.
    const scale = Math.max(sBcr.width / vw, sBcr.height / vh);
    const dispW = vw * scale;
    const dispH = vh * scale;
    const offX = (sBcr.width - dispW) / 2;   // negativo si vídeo más ancho que stream
    const offY = (sBcr.height - dispH) / 2;

    // Reticle en coordenadas del display (relativo al .stream)
    const rdx = rBcr.left - sBcr.left;
    const rdy = rBcr.top - sBcr.top;

    // Convertir a coordenadas del frame del video
    let sx = (rdx - offX) / scale;
    let sy = (rdy - offY) / scale;
    let sw = rBcr.width / scale;
    let sh = rBcr.height / scale;

    // Clamp dentro del frame
    sx = Math.max(0, Math.min(vw - 1, sx));
    sy = Math.max(0, Math.min(vh - 1, sy));
    sw = Math.max(1, Math.min(vw - sx, sw));
    sh = Math.max(1, Math.min(vh - sy, sh));
    return { sx, sy, sw, sh };
  }

  async function _decodeRoi() {
    if (state.closed) return;
    const roi = _computeROI();
    if (!roi) { state.scanTimer = setTimeout(_decodeRoi, 100); return; }

    roiCanvas.width = Math.floor(roi.sw);
    roiCanvas.height = Math.floor(roi.sh);
    try {
      roiCtx.drawImage(video, roi.sx, roi.sy, roi.sw, roi.sh, 0, 0, roiCanvas.width, roiCanvas.height);
    } catch {
      state.scanTimer = setTimeout(_decodeRoi, 100);
      return;
    }

    try {
      const result = await state.reader.decodeFromCanvas(roiCanvas);
      if (result && !state.closed) {
        const value = result.getText();
        const format = BarcodeFormat[result.getBarcodeFormat()] || String(result.getBarcodeFormat());
        const now = Date.now();
        if (!(value === state.lastValue && (now - state.lastTs) < DEDUP_MS)) {
          state.lastValue = value;
          state.lastTs = now;
          _beep(); _vibrate(50);
          let action = 'continue';
          try { action = onDecode?.(value, format) || 'continue'; }
          catch (e) { console.error('onDecode falló', e); }
          if (action === 'consume') { close(); return; }
        }
      }
    } catch (e) {
      // NotFoundException es el caso normal cuando no hay código en el ROI;
      // no la loguamos para no contaminar la consola.
      if (e?.name && e.name !== 'NotFoundException' && e.name !== 'NotFoundException2') {
        // (algunas builds usan NotFoundException2)
      }
    }
    // Frecuencia: ~10 fps para no quemar CPU del móvil.
    state.scanTimer = setTimeout(_decodeRoi, 100);
  }

  async function startStream() {
    // Cierra stream previo si lo había (al cambiar de cámara)
    if (state.stream) {
      try { state.stream.getTracks().forEach(t => t.stop()); } catch {}
      state.stream = null;
    }
    const dev = state.devices[state.deviceIdx];
    state.stream = await navigator.mediaDevices.getUserMedia({
      video: dev?.deviceId
        ? { deviceId: { exact: dev.deviceId } }
        : { facingMode: { ideal: 'environment' } },
      audio: false,
    });
    video.srcObject = state.stream;
    await video.play().catch(() => {});

    // Arrancar el loop de decodificación con ROI
    if (state.scanTimer) clearTimeout(state.scanTimer);
    state.scanTimer = setTimeout(_decodeRoi, 300);  // pequeño delay para que el video tenga dims

    setTimeout(() => {
      _detectTorch(); _detectZoom(); _continuousFocus(); _updateMeta();
    }, 600);
  }

  function _track() { return video?.srcObject?.getVideoTracks?.()[0]; }

  function _detectTorch() {
    const t = _track(); if (!t || !t.getCapabilities) return;
    btnTorch.hidden = !t.getCapabilities().torch;
  }
  function _detectZoom() {
    const t = _track(); if (!t || !t.getCapabilities) return;
    const caps = t.getCapabilities();
    if (!caps.zoom || caps.zoom.max <= caps.zoom.min) return;
    state.zoom.supported = true;
    state.zoom.min = caps.zoom.min; state.zoom.max = caps.zoom.max;
    state.zoom.step = caps.zoom.max - caps.zoom.min >= 3 ? Math.max(0.5, caps.zoom.step || 0.1) : (caps.zoom.step || 0.1);
    state.zoom.current = t.getSettings().zoom ?? caps.zoom.min;
    btnZoomIn.hidden = false; btnZoomOut.hidden = false;
  }
  async function _continuousFocus() {
    const t = _track(); if (!t || !t.applyConstraints) return;
    const caps = t.getCapabilities?.() || {};
    if (!(caps.focusMode || []).includes('continuous')) return;
    try { await t.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch {}
  }
  function _updateMeta() {
    const t = _track(); if (!t) return;
    const s = t.getSettings();
    camMeta.textContent = `${s.width || video.videoWidth || '?'}×${s.height || video.videoHeight || '?'}${s.frameRate ? ' · ' + Math.round(s.frameRate) + 'fps' : ''}`;
  }
  async function _setZoom(z) {
    if (!state.zoom.supported) return;
    state.zoom.current = Math.min(state.zoom.max, Math.max(state.zoom.min, z));
    try { await _track()?.applyConstraints({ advanced: [{ zoom: state.zoom.current }] }); } catch {}
  }

  btnSwap.addEventListener('click', () => {
    if (state.devices.length < 2) return;
    state.deviceIdx = (state.deviceIdx + 1) % state.devices.length;
    startStream();
  });
  btnTorch.addEventListener('click', async () => {
    const t = _track(); if (!t) return;
    state.torchOn = !state.torchOn;
    try { await t.applyConstraints({ advanced: [{ torch: state.torchOn }] }); } catch { state.torchOn = false; }
  });
  btnZoomIn.addEventListener('click',  () => _setZoom(state.zoom.current + state.zoom.step));
  btnZoomOut.addEventListener('click', () => _setZoom(state.zoom.current - state.zoom.step));

  try {
    await startStream();
  } catch (e) {
    console.error('Cámara falló:', e);
    overlay.querySelector('.stream').insertAdjacentHTML('beforeend',
      `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-align:center;padding:1rem;">
         No se pudo abrir la cámara.<br>${escapeHtml(e?.name || e?.message || 'error desconocido')}
       </div>`);
  }

  // Función para que el caller pinte feedback en la banda inferior.
  // Acepta string (verde) o {text, tone: 'ok'|'warn'|'error'}.
  const statusEl = overlay.querySelector('#cam-status');
  close.setStatus = (msg) => {
    if (!statusEl) return;
    if (!msg) { statusEl.hidden = true; return; }
    const text = typeof msg === 'string' ? msg : msg.text;
    const tone = typeof msg === 'string' ? 'ok' : (msg.tone || 'ok');
    const colorByTone = {
      ok:    'rgba(4, 120, 87, 0.92)',   // verde --c-done
      warn:  'rgba(217, 119, 6, 0.92)',  // naranja --c-partial
      error: 'rgba(185, 28, 28, 0.92)',  // rojo --c-error
    };
    statusEl.style.background = colorByTone[tone] || colorByTone.ok;
    statusEl.textContent = text;
    statusEl.hidden = false;
  };

  return close;
}
